import { Button } from '@fisura/sim';

const HALF_PI = Math.PI / 2;

/**
 * Captures keyboard + pointer-lock mouse input and turns it into the same
 * button bitfield + view angles the simulation consumes. The render loop calls
 * {@link sample} once per simulation tick.
 */
export class InputController {
  private readonly pressed = new Set<string>();
  private yaw: number;
  private pitch = 0;
  private locked = false;
  private firing = false;
  // A press latched on mouse-down so a tap shorter than one frame (common on
  // trackpads) still fires a shot, even though `firing` is already false again
  // by the time the render loop samples it.
  private tapQueued = false;
  private throwQueued = false; // G pressed — grenade
  private aiming = false; // right mouse button — ADS
  private weaponSel = 0; // 0=SMG 1=Shotgun 2=Rifle 3=Rocket
  private nadeSel = 0; // selected grenade: 0=Frag 1=Smoke 2=Flashbang (cycle with B)

  /** Radians of view rotation per pixel of mouse movement. */
  sensitivity = 0.0022;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    initialYaw = 0,
  ) {
    this.yaw = initialYaw;

    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.onBlur);
    document.addEventListener('pointerlockchange', this.onLockChange);
    document.addEventListener('mousemove', this.onMouseMove);
    // Both Pointer Events AND legacy mouse events drive firing/aiming. Firefox
    // and Safari don't reliably dispatch `mousedown`/`mouseup` for a trackpad
    // tap while the pointer is locked (Chrome does), but they do dispatch the
    // standardized `pointerdown`/`pointerup`. Every handler only flips booleans
    // idempotently, so listening to both never double-fires a shot.
    document.addEventListener('pointerdown', this.onMouseDown);
    document.addEventListener('pointerup', this.onMouseUp);
    document.addEventListener('mousedown', this.onMouseDown);
    document.addEventListener('mouseup', this.onMouseUp);
    canvas.addEventListener('contextmenu', (e) => e.preventDefault());
  }

  get isLocked(): boolean {
    return this.locked;
  }

  get isFiring(): boolean {
    return this.firing;
  }

  /** Right mouse button held — aiming down sights. */
  get isAiming(): boolean {
    return this.aiming;
  }

  /** Consume a one-shot trigger pull latched since the last call (tap support). */
  dequeueTap(): boolean {
    if (!this.tapQueued) return false;
    this.tapQueued = false;
    return true;
  }

  /** Consume a queued grenade throw (G key). */
  dequeueThrow(): boolean {
    if (!this.throwQueued) return false;
    this.throwQueued = false;
    return true;
  }

  requestLock(): void {
    void this.canvas.requestPointerLock();
  }

  /** Build the input command for the current tick. */
  sample(seq: number): {
    seq: number;
    buttons: number;
    yaw: number;
    pitch: number;
    weaponId: number;
  } {
    let buttons = 0;
    if (this.pressed.has('KeyW')) buttons |= Button.Forward;
    if (this.pressed.has('KeyS')) buttons |= Button.Back;
    if (this.pressed.has('KeyA')) buttons |= Button.Left;
    if (this.pressed.has('KeyD')) buttons |= Button.Right;
    if (this.firing) buttons |= Button.Fire;
    if (this.aiming) buttons |= Button.Ads;
    if (this.pressed.has('Space')) buttons |= Button.Jump;
    if (this.pressed.has('KeyR')) buttons |= Button.Reload;
    // You RUN by default; holding Shift walks (quiet + accurate). The wire
    // semantics of Button.Sprint are unchanged — bots still set it explicitly.
    if (!this.pressed.has('ShiftLeft') && !this.pressed.has('ShiftRight'))
      buttons |= Button.Sprint;
    if (
      this.pressed.has('ControlLeft') ||
      this.pressed.has('ControlRight') ||
      this.pressed.has('KeyC')
    )
      buttons |= Button.Crouch;
    return { seq, buttons, yaw: this.yaw, pitch: this.pitch, weaponId: this.weapon };
  }

  /** Current view angles (read every render frame for crisp mouse-look). */
  get view(): { yaw: number; pitch: number } {
    return { yaw: this.yaw, pitch: this.pitch };
  }

  /** Programmatically set the view angles (e.g. restore after a page reload). */
  setView(yaw: number, pitch: number): void {
    this.yaw = yaw;
    this.pitch = pitch;
  }

  /** Currently equipped weapon id (switched with 1/2/3). */
  get weapon(): number {
    return this.weaponSel;
  }

  /** Currently selected grenade type (0=Frag 1=Smoke 2=Flashbang; cycled with B). */
  get grenade(): number {
    return this.nadeSel;
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.onBlur);
    document.removeEventListener('pointerlockchange', this.onLockChange);
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('pointerdown', this.onMouseDown);
    document.removeEventListener('pointerup', this.onMouseUp);
    document.removeEventListener('mousedown', this.onMouseDown);
    document.removeEventListener('mouseup', this.onMouseUp);
  }

  private readonly onKeyDown = (e: KeyboardEvent): void => {
    // Don't scroll the page on Space; don't trigger browser shortcuts mid-game.
    if (e.code === 'Space' || e.code.startsWith('Arrow')) e.preventDefault();
    this.pressed.add(e.code);
    // Weapon switch: 1 = SMG, 2 = Shotgun, 3 = Rifle, 4 = Rocket.
    if (e.code === 'Digit1') this.weaponSel = 0;
    else if (e.code === 'Digit2') this.weaponSel = 1;
    else if (e.code === 'Digit3') this.weaponSel = 2;
    else if (e.code === 'Digit4') this.weaponSel = 3;
    else if (e.code === 'KeyG' && this.locked) this.throwQueued = true;
    // B cycles the selected grenade type (Frag → Smoke → Flashbang → …).
    else if (e.code === 'KeyB' && this.locked) this.nadeSel = (this.nadeSel + 1) % 3;
  };

  private readonly onKeyUp = (e: KeyboardEvent): void => {
    this.pressed.delete(e.code);
  };

  private readonly onBlur = (): void => {
    this.pressed.clear(); // avoid "stuck key" when focus is lost
    this.firing = false;
    this.aiming = false;
    this.tapQueued = false;
  };

  private readonly onLockChange = (): void => {
    this.locked = document.pointerLockElement === this.canvas;
    if (!this.locked) {
      this.pressed.clear();
      this.firing = false;
      this.aiming = false;
      this.tapQueued = false;
    }
  };

  private readonly onMouseDown = (e: MouseEvent): void => {
    if (this.locked && e.button === 0) {
      this.firing = true;
      this.tapQueued = true;
    }
    if (this.locked && e.button === 2) this.aiming = true;
  };

  private readonly onMouseUp = (e: MouseEvent): void => {
    if (e.button === 0) this.firing = false;
    if (e.button === 2) this.aiming = false;
  };

  private readonly onMouseMove = (e: MouseEvent): void => {
    if (!this.locked) return;
    this.yaw += e.movementX * this.sensitivity;
    this.pitch -= e.movementY * this.sensitivity;
    // Wrap yaw to keep it bounded; clamp pitch to avoid gimbal flip.
    if (this.yaw > Math.PI) this.yaw -= 2 * Math.PI;
    else if (this.yaw < -Math.PI) this.yaw += 2 * Math.PI;
    this.pitch = Math.max(-HALF_PI + 0.01, Math.min(HALF_PI - 0.01, this.pitch));
  };
}
